import { useState } from 'react';
import { Input, Text, Button, ButtonGroup, StatusChip } from '@fragments';
import { IconCode, IconKey, IconListDetails, IconPlus, IconSend, IconUser, IconWebhook, IconX } from '@tabler/icons-react';
import {
    WEBHOOK_AUTH_TYPES,
    WEBHOOK_CONTENT_TYPES,
    WEBHOOK_FIELD_SOURCES,
    WEBHOOK_METHODS,
} from '../../../enums/actionTypes';
import JsonTemplateEditor from '../JsonTemplateEditor';
import './styles.scss';

const METHOD_OPTIONS = WEBHOOK_METHODS.map(m => ({ value: m, label: m }));

const isMasked = value => typeof value === 'string' && value.startsWith('***');

function secretHelper(value) {
    if (!isMasked(value)) return undefined;
    return 'Valor salvo. Edite para substituir; deixe como está para manter.';
}

function staticFieldsToRows(staticFields) {
    return Object.entries(staticFields ?? {}).map(([key, value]) => ({ key, value: String(value ?? '') }));
}

function rowsToStaticFields(rows) {
    const out = {};
    rows.forEach(({ key, value }) => {
        if (key.trim()) out[key.trim()] = value;
    });
    return out;
}

function TestResult({ testState }) {
    if (!testState || testState.loading) return null;

    if (testState.error) {
        return (
            <div className="webhook-builder__test-result">
                <div className="webhook-builder__test-result-header">
                    <StatusChip.Error text="Falha no teste" />
                </div>
                <Text.span contrast={7}>{testState.error}</Text.span>
            </div>
        );
    }

    if (!testState.result) return null;

    const { response_code: code, response_body: body } = testState.result;
    const succeeded = code >= 200 && code < 300;
    const Chip = succeeded ? StatusChip.Success : StatusChip.Error;

    return (
        <div className="webhook-builder__test-result">
            <div className="webhook-builder__test-result-header">
                <Chip text={`HTTP ${code}`} />
                <Text.caption>Resposta do endpoint</Text.caption>
            </div>
            {body
                ? <pre className="webhook-builder__test-result-body">{body}</pre>
                : <Text.span contrast={5}>Resposta sem corpo.</Text.span>}
        </div>
    );
}

export default function WebhookBuilder({ config, onChange, onTest, testState }) {
    const auth = config?.auth ?? { type: 'none' };
    const headers = config?.headers ?? [];
    const fieldMappings = config?.field_mappings ?? [];

    // static_fields is an object in the contract; rows are local UI state so
    // keys stay editable without churning object identity on each keystroke.
    const [staticRows, setStaticRows] = useState(() => staticFieldsToRows(config?.static_fields));

    // The backend uses body_template whenever it is set, ignoring mappings —
    // the mode switch makes that either/or explicit. The draft keeps the
    // template text alive while the user is on the mapping side (config only
    // carries it in template mode, so what you see is what fires).
    const [bodyMode, setBodyMode] = useState(() => (config?.body_template ? 'template' : 'mapping'));
    const [templateDraft, setTemplateDraft] = useState(() => config?.body_template ?? '');

    function handleBodyModeChange(mode) {
        if (mode === bodyMode) return;
        setBodyMode(mode);
        patch({
            body_template: mode === 'template' && templateDraft.trim() ? templateDraft : null,
        });
    }

    function handleTemplateChange(v) {
        setTemplateDraft(v);
        patch({ body_template: v.trim() ? v : null });
    }

    // Full validation runs on click (useManageIntegration); the button only
    // gates on the two fields without which a test is meaningless.
    const canTest = /^https:\/\/./i.test((config?.url ?? '').trim())
        && WEBHOOK_METHODS.includes(config?.method);

    function patch(partial) {
        onChange({ ...config, ...partial });
    }

    function patchAuth(partial) {
        patch({ auth: { ...auth, ...partial } });
    }

    // Switching auth type discards previous credentials so stale secrets
    // (possibly masked) never leak into another auth shape on submit.
    function handleAuthTypeChange(type) {
        patch({ auth: { type } });
    }

    function updateRow(list, index, partial) {
        return list.map((row, i) => (i === index ? { ...row, ...partial } : row));
    }

    function updateStaticRows(next) {
        setStaticRows(next);
        patch({ static_fields: rowsToStaticFields(next) });
    }

    return (
        <div className="webhook-builder">
            {/* ── Endpoint ─────────────────────────────────────────────── */}
            <div className="webhook-builder__section">
                <Text.subheading contrast={6}>Endpoint</Text.subheading>
                <div className="webhook-builder__endpoint-row">
                    <div className="webhook-builder__method">
                        <Input.Select
                            label="Método"
                            value={config?.method ?? 'POST'}
                            onInput={v => patch({ method: v })}
                            options={METHOD_OPTIONS}
                        />
                    </div>
                    <div className="webhook-builder__url">
                        <Input.Text
                            label="URL"
                            placeholder="https://hooks.exemplo.com/abc"
                            value={config?.url ?? ''}
                            onInput={v => patch({ url: v })}
                            start={<IconWebhook size={16} />}
                        />
                    </div>
                </div>
            </div>

            {/* ── Autenticação ─────────────────────────────────────────── */}
            <div className="webhook-builder__section">
                <Text.subheading contrast={6}>Autenticação</Text.subheading>
                <Input.Select
                    label="Tipo de autenticação"
                    value={auth.type ?? 'none'}
                    onInput={handleAuthTypeChange}
                    options={WEBHOOK_AUTH_TYPES}
                />

                {auth.type === 'bearer' && (
                    <Input.Text
                        label="Token"
                        placeholder="Insira o bearer token"
                        value={auth.token ?? ''}
                        onInput={v => patchAuth({ token: v })}
                        start={<IconKey size={16} />}
                        helper={secretHelper(auth.token)}
                    />
                )}

                {auth.type === 'basic' && (
                    <>
                        <Input.Text
                            label="Usuário"
                            placeholder="Insira o usuário"
                            value={auth.username ?? ''}
                            onInput={v => patchAuth({ username: v })}
                            start={<IconUser size={16} />}
                        />
                        <Input.Text
                            label="Senha"
                            placeholder="Insira a senha"
                            value={auth.password ?? ''}
                            onInput={v => patchAuth({ password: v })}
                            start={<IconKey size={16} />}
                            helper={secretHelper(auth.password)}
                        />
                    </>
                )}

                {auth.type === 'api_key' && (
                    <>
                        <Input.Text
                            label="Nome do cabeçalho"
                            placeholder="X-Api-Key"
                            value={auth.header_name ?? ''}
                            onInput={v => patchAuth({ header_name: v })}
                        />
                        <Input.Text
                            label="Valor"
                            placeholder="Insira a chave"
                            value={auth.value ?? ''}
                            onInput={v => patchAuth({ value: v })}
                            start={<IconKey size={16} />}
                            helper={secretHelper(auth.value)}
                        />
                    </>
                )}
            </div>

            {/* ── Cabeçalhos ───────────────────────────────────────────── */}
            <div className="webhook-builder__section">
                <div className="webhook-builder__section-header">
                    <Text.subheading contrast={6}>Cabeçalhos personalizados</Text.subheading>
                    <Button
                        startIcon={<IconPlus size={16} />}
                        onClick={() => patch({ headers: [...headers, { key: '', value: '' }] })}
                        color="overlay"
                        variant="dashed"
                        size="small"
                    >
                        Adicionar
                    </Button>
                </div>

                {headers.length === 0 ? (
                    <Text.span contrast={5}>Nenhum cabeçalho adicional.</Text.span>
                ) : headers.map((header, idx) => (
                    <div className="webhook-builder__kv-row" key={idx}>
                        <Input.Text
                            placeholder="Nome (ex: X-Source)"
                            value={header.key ?? ''}
                            onInput={v => patch({ headers: updateRow(headers, idx, { key: v }) })}
                        />
                        <Input.Text
                            placeholder="Valor"
                            value={header.value ?? ''}
                            onInput={v => patch({ headers: updateRow(headers, idx, { value: v }) })}
                        />
                        <Button
                            onClick={() => patch({ headers: headers.filter((_, i) => i !== idx) })}
                            icon={<IconX size={16} />}
                            color="overlay"
                            size="small"
                            tooltip="Remover cabeçalho"
                        />
                    </div>
                ))}
            </div>

            {/* ── Corpo / Mapeamento ───────────────────────────────────── */}
            <div className="webhook-builder__section">
                <div className="webhook-builder__section-header">
                    <Text.subheading contrast={6}>Corpo da requisição</Text.subheading>
                </div>

                <Input.Select
                    label="Content-Type"
                    value={config?.content_type ?? 'application/json'}
                    onInput={v => patch({ content_type: v })}
                    options={WEBHOOK_CONTENT_TYPES}
                />

                <div className="webhook-builder__subsection-header pt-1">
                    <Text.label contrast={8}>Configuração do corpo da requisição</Text.label>
                </div>
                <ButtonGroup
                    grow
                    size="small"
                    value={bodyMode}
                    onChange={handleBodyModeChange}
                    options={[
                        { value: 'mapping', label: 'Mapeamento de campos', icon: <IconListDetails size={16} /> },
                        { value: 'template', label: 'JSON personalizado', icon: <IconCode size={16} /> },
                    ]}
                />
                <Text.span contrast={5}>
                    {bodyMode === 'mapping'
                        ? 'Monte o corpo escolhendo os dados e o nome de cada campo — o formato recomendado.'
                        : 'Escreva o corpo em JSON livre com variáveis {{...}} — substitui o mapeamento de campos.'}
                </Text.span>

                {bodyMode === 'mapping' && (<>
                <div className="webhook-builder__subsection-header">
                    <Text.label contrast={8}>Mapeamento de campos</Text.label>
                    <Button
                        startIcon={<IconPlus size={16} />}
                        onClick={() => patch({ field_mappings: [...fieldMappings, { source: '', target: '' }] })}
                        color="overlay"
                        variant="dashed"
                        size="small"
                    >
                        Adicionar
                    </Button>
                </div>
                <Text.span contrast={5}>
                    Escolha quais dados do comprador serão enviados e com qual nome de campo.
                </Text.span>

                {fieldMappings.map((mapping, idx) => (
                    <div className="webhook-builder__kv-row" key={idx}>
                        <Input.Select
                            placeholder="Dado do comprador"
                            value={mapping.source ?? ''}
                            onInput={v => patch({ field_mappings: updateRow(fieldMappings, idx, { source: v }) })}
                            options={WEBHOOK_FIELD_SOURCES}
                        />
                        <Input.Text
                            placeholder="Nome do campo no destino"
                            value={mapping.target ?? ''}
                            onInput={v => patch({ field_mappings: updateRow(fieldMappings, idx, { target: v }) })}
                        />
                        <Button
                            onClick={() => patch({ field_mappings: fieldMappings.filter((_, i) => i !== idx) })}
                            icon={<IconX size={16} />}
                            color="overlay"
                            size="small"
                            tooltip="Remover mapeamento"
                        />
                    </div>
                ))}

                <div className="webhook-builder__subsection-header">
                    <Text.label contrast={8}>Campos fixos</Text.label>
                    <Button
                        startIcon={<IconPlus size={16} />}
                        onClick={() => updateStaticRows([...staticRows, { key: '', value: '' }])}
                        color="overlay"
                        variant="dashed"
                        size="small"
                    >
                        Adicionar
                    </Button>
                </div>
                <Text.span contrast={5}>
                    Valores fixos incluídos em todo envio (ex: origin = ginastee).
                </Text.span>

                {staticRows.map((row, idx) => (
                    <div className="webhook-builder__kv-row" key={idx}>
                        <Input.Text
                            placeholder="Nome do campo"
                            value={row.key}
                            onInput={v => updateStaticRows(updateRow(staticRows, idx, { key: v }))}
                        />
                        <Input.Text
                            placeholder="Valor"
                            value={row.value}
                            onInput={v => updateStaticRows(updateRow(staticRows, idx, { value: v }))}
                        />
                        <Button
                            onClick={() => updateStaticRows(staticRows.filter((_, i) => i !== idx))}
                            icon={<IconX size={16} />}
                            color="overlay"
                            size="small"
                            tooltip="Remover campo"
                        />
                    </div>
                ))}
                </>)}

                {bodyMode === 'template' && (
                    <JsonTemplateEditor
                        placeholder={'{ "cliente": "{{name}}", "cobranca": "{{payment.id}}" }'}
                        value={templateDraft}
                        onChange={handleTemplateChange}
                        variables={WEBHOOK_FIELD_SOURCES}
                        helper="Digite {{ para ver as variáveis disponíveis; Tab formata o JSON."
                    />
                )}
            </div>

            {/* ── Teste ────────────────────────────────────────────────── */}
            {onTest && (
                <div className="webhook-builder__section">
                    <div className="webhook-builder__section-header">
                        <Text.subheading contrast={6}>Teste</Text.subheading>
                        <Button
                            startIcon={<IconSend size={16} />}
                            onClick={onTest}
                            color="overlay"
                            size="small"
                            loading={testState?.loading}
                            disabled={!canTest}
                        >
                            Testar webhook
                        </Button>
                    </div>
                    <Text.span contrast={5}>
                        Envia uma chamada de teste com os seus dados de usuário para o endpoint configurado, sem registrar nos logs.
                    </Text.span>
                    <TestResult testState={testState} />
                </div>
            )}
        </div>
    );
}
